import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, subscribeSubmissionStatus } from '@/api/client';
import type { DisputeBlockedReason, SubmissionDetails, SubmissionStatus } from '@/api/types';
import { BigCTA } from '@/components/BigCTA';
import { StatusChip } from '@/components/StatusChip';
import { COMPANY_NAMES } from '@/components/CompanyLogo';
import { ErrorView, formatError } from '@/components/ErrorView';
import { FullPageSpinner } from '@/components/Spinner';

// Must match SubmissionStatusStreamController.isTerminal() — the server closes the stream
// immediately for these, so subscribing to one just opens a socket that dies on arrival.
const TERMINAL: SubmissionStatus[] = ['APPROVED', 'REJECTED', 'MANUAL_REVIEW', 'SHADOWBANNED'];

export function SubmissionDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<SubmissionDetails | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeMessage, setDisputeMessage] = useState('');
  const [disputeStatus, setDisputeStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [disputeError, setDisputeError] = useState<string | null>(null);

  // Initial fetch.
  useEffect(() => {
    if (!id) return;
    let off = false;
    api.getSubmissionDetails(id)
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setErr(e); });
    return () => { off = true; };
  }, [id]);

  // Live status while the submission is still being processed.
  //
  // The server closes the stream after 5 minutes, and a submission can legitimately outlive that
  // (queue retry, LLM outage, StuckSubmissionSweeper). A single subscription would then leave the
  // screen frozen on "Обработка" forever, so re-subscribe when the stream ends and keep a slow
  // poll running as a backstop for the case where SSE itself is unavailable.
  const status = data?.status;
  useEffect(() => {
    if (!id || !status || TERMINAL.includes(status)) return;
    let stopped = false;
    let unsub: (() => void) | undefined;

    const refetch = () => api.getSubmissionDetails(id).then(setData).catch(() => { /* transient */ });

    const connect = () => {
      if (stopped) return;
      unsub = subscribeSubmissionStatus(
        id,
        (evt) => { if (evt.status !== status) void refetch(); },
        () => { if (!stopped) reconnect(); },
        () => { if (!stopped) reconnect(); },
      );
    };
    // The stream ending is the normal case at the server's timeout, not an error — in both
    // situations re-check the status first, then reopen if it is still non-terminal.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const reconnect = () => {
      timer = setTimeout(() => { void refetch(); connect(); }, 3000);
    };

    connect();
    const poll = setInterval(() => { void refetch(); }, 30000);
    return () => {
      stopped = true;
      unsub?.();
      if (timer) clearTimeout(timer);
      clearInterval(poll);
    };
  }, [id, status]);

  if (err && !data) return <div className="page"><ErrorView error={err} /></div>;
  if (!data) return <FullPageSpinner />;

  const sendDispute = async () => {
    if (!id) return;
    setDisputeStatus('sending');
    setDisputeError(null);
    try {
      await api.openDispute({ submissionId: id, message: disputeMessage.trim() || undefined });
      setDisputeStatus('sent');
      setShowDisputeForm(false);
    } catch (e) {
      setDisputeStatus('error');
      setDisputeError(formatError(e));
    }
  };

  return (
    <div className="page">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="big-cta secondary"
        style={{ alignSelf: 'flex-start', minHeight: 'auto', padding: '8px 14px', fontSize: 14 }}
      >
        ← Назад
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 className="page-title" style={{ flex: 1, margin: 0 }}>{COMPANY_NAMES[data.company]}</h1>
        <StatusChip status={data.status} />
      </div>

      {data.payoutRub != null && data.payoutRub > 0 && (
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="balance-amount" style={{ fontSize: 32 }}>
            {fmt(data.payoutRub)} ₽
          </div>
          <div className="balance-label">Начислено</div>
        </div>
      )}

      {data.parsed && (
        <section>
          <h2 style={{ fontSize: 16, color: 'var(--bk-hint)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Распознано LLM
          </h2>
          <div className="card" style={{ padding: 0 }}>
            {data.parsed.totalEarned != null && <Row label="Заработано" value={`${fmt(data.parsed.totalEarned)} ₽`} />}
            {data.parsed.ordersCount != null && <Row label="Заказов" value={String(data.parsed.ordersCount)} />}
            {data.parsed.hoursOnline != null && <Row label="Часов онлайн" value={fmt(data.parsed.hoursOnline)} />}
            {data.parsed.bonuses != null && <Row label="Бонусы" value={`${fmt(data.parsed.bonuses)} ₽`} />}
            <Row label="Уверенность" value={`${data.parsed.confidenceScore}%`} last />
          </div>
        </section>
      )}

      {data.derived && (data.derived.eph != null || data.derived.epo != null || data.derived.oph != null) && (
        <section>
          <h2 style={{ fontSize: 16, color: 'var(--bk-hint)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Метрики
          </h2>
          <div className="stat-row">
            {data.derived.eph != null && (
              <div className="stat-tile">
                <div className="value">{Math.round(data.derived.eph)} ₽</div>
                <div className="label">в час (EPH)</div>
              </div>
            )}
            {data.derived.epo != null && (
              <div className="stat-tile">
                <div className="value">{Math.round(data.derived.epo)} ₽</div>
                <div className="label">за заказ (EPO)</div>
              </div>
            )}
          </div>
        </section>
      )}

      {data.rejectionReasons.length > 0 && (
        <section>
          <h2 style={{ fontSize: 16, color: 'var(--bk-destructive)', margin: '0 0 8px' }}>
            {/* The same flags are attached while a report is still with a moderator, where
                "Причины отклонения" would wrongly read as a final verdict. */}
            {data.status === 'MANUAL_REVIEW' ? 'Что насторожило проверку' : 'Причины отклонения'}
          </h2>
          <div className="card">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {data.rejectionReasons.map((r) => <li key={r} style={{ marginBottom: 4 }}>{prettyReason(r)}</li>)}
            </ul>
          </div>
        </section>
      )}

      {DISPUTABLE.includes(data.status) && disputeStatus !== 'sent' && !data.canDispute && (
        <div className="card" style={{ color: 'var(--bk-hint)', fontSize: 14 }}>
          {disputeBlockedText(data.disputeBlockedBy, data.disputeDeadline)}
        </div>
      )}

      {data.canDispute && disputeStatus !== 'sent' && (
        <>
          {!showDisputeForm ? (
            <>
              <BigCTA variant="secondary" onClick={() => setShowDisputeForm(true)}>
                Это ошибка, проверьте вручную
              </BigCTA>
              <div style={{ color: 'var(--bk-hint)', fontSize: 12, textAlign: 'center', marginTop: -8 }}>
                {data.disputeDeadline
                  ? `Оспорить можно до ${formatDeadline(data.disputeDeadline)}`
                  : 'Отчёт ещё на проверке — срок оспаривания начнётся после решения'}
              </div>
            </>
          ) : (
            <div className="card">
              {disputeError && <div className="error-banner" style={{ marginBottom: 12 }}>{disputeError}</div>}
              <div className="field">
                <div className="field-label">Сообщение модератору (опц.)</div>
                <textarea
                  className="field-input"
                  style={{ minHeight: 96, paddingTop: 10, fontFamily: 'inherit' }}
                  value={disputeMessage}
                  onChange={(e) => setDisputeMessage(e.target.value)}
                  maxLength={500}
                  placeholder="Например: скриншот сделан сегодня в 22:00, EXIF проверьте"
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="big-cta secondary" style={{ flex: 1 }} onClick={() => setShowDisputeForm(false)}>
                  Отмена
                </button>
                <button className="big-cta" style={{ flex: 1 }} onClick={sendDispute} disabled={disputeStatus === 'sending'}>
                  {disputeStatus === 'sending' ? 'Отправляем…' : 'Отправить'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {disputeStatus === 'sent' && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--bk-success)' }}>
          ✅ Спор отправлен модератору. Ответ придёт в Telegram.
        </div>
      )}

      {data.screenshots.length > 0 && (
        <section>
          <h2 style={{ fontSize: 16, color: 'var(--bk-hint)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Скриншоты
          </h2>
          <div className="thumb-grid">
            {data.screenshots.map((s, i) => (
              <div className="thumb" key={i}>
                <img src={s.url} alt={`Screenshot ${i + 1}`} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const fmt = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });

/** Statuses the backend would accept a dispute for — see DisputeService.eligibility. */
const DISPUTABLE: SubmissionStatus[] = ['REJECTED', 'MANUAL_REVIEW'];

function formatDeadline(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

function disputeBlockedText(reason: DisputeBlockedReason | null, deadline: string | null): string {
  switch (reason) {
    case 'ALREADY_OPEN':
      return '✅ Спор по этому отчёту уже открыт. Ответ придёт в Telegram.';
    case 'WINDOW_EXPIRED':
      // WINDOW_EXPIRED без даты недостижим — срок не истекает, пока его нет.
      return deadline ? `Срок оспаривания истёк ${formatDeadline(deadline)}.` : 'Срок оспаривания истёк.';
    case 'DAILY_LIMIT':
      return 'Сегодня вы открыли слишком много споров. Попробуйте завтра.';
    default:
      return 'Оспорить этот отчёт сейчас нельзя.';
  }
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', padding: '12px 14px',
        borderBottom: last ? 'none' : '1px solid var(--bk-border)',
      }}
    >
      <span style={{ color: 'var(--bk-hint)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ru.bazakurierov.submission.RejectionReason
const REASON_LABEL: Record<string, string> = {
  PHOTO_OF_SCREEN: 'Похоже на фотографию экрана, а не скриншот',
  DUPLICATE: 'Такой скриншот уже загружали.',
  EXIF_MISMATCH: 'Дата съёмки скриншота не совпадает с датой смены.',
  INVALID_RESOLUTION: 'Это не похоже на скриншот телефона — проверьте пропорции картинки.',
  NOT_COURIER_APP: 'На скриншоте не найден экран курьерского приложения.',
  DATA_ANOMALY: 'Цифры выглядят неправдоподобно.',
  MATH_MISMATCH: 'Цифры на скриншоте не сходятся между собой.',
  STATISTICAL_OUTLIER: 'Показатели сильно отличаются от обычных по вашему району.',
  LLM_UNREADABLE: 'Не удалось разобрать текст на скриншоте — сделайте фото чётче.',
  INPUT_MISMATCH: 'Данные в форме не совпадают с тем, что на скриншоте.',
};

function prettyReason(code: string): string {
  return REASON_LABEL[code] ?? code.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}
